import { ButtonProps } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  getLanguageLabel,
  IQuoteRespSchema,
  OUTPUT_LANGUAGE_OPTIONS,
  QuoteOutputLanguage,
  QuoteOutputLanguageSchema,
} from '@monetize/types/app';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { handleApiErrorToast } from '../../api/axios';
import { useUpdateOutputLanguageOnQuote } from '../../api/cpqService';
import { getQuoteStatus } from '../../utils';
import {
  MBox,
  MButton,
  MFormField,
  MText,
  QuoteSelectInput,
} from '../Monetize';
import { BlankTextOrContent } from '../Monetize/BlankTextOrContent';
import { MCustomSelect } from '../Monetize/MCustomSelect/MCustomSelect';

export const QuoteOutputLanguageSelection = ({
  quote,
  fetchQuote,
  setQuote,
  setIsQuoteSaving,
  buttonProps,
}: {
  quote: IQuoteRespSchema;
  fetchQuote: (quoteId: string) => Promise<IQuoteRespSchema | null>;
  setQuote: (quote: IQuoteRespSchema | null) => void;
  setIsQuoteSaving: (val: boolean) => void;
  buttonProps?: ButtonProps;
}) => {
  const { mutateAsync: doUpdateOutputLanguage } =
    useUpdateOutputLanguageOnQuote();
  const { isReview, isApproved, isDraft, isAccepted, isAcceptedAdminEditable } =
    getQuoteStatus(quote);
  const canEditOutputLanguage =
    isReview || isApproved || isDraft || isAccepted || isAcceptedAdminEditable;

  const [editMode, setEditMode] = useState(false);
  const {
    control,
    reset,
    formState: { errors },
  } = useForm<QuoteOutputLanguage>({
    resolver: zodResolver(QuoteOutputLanguageSchema),
    mode: 'onChange',
    defaultValues: { outputLanguage: '' },
  });

  useEffect(() => {
    reset({ outputLanguage: quote.outputLanguage || null });
  }, [quote.outputLanguage, reset]);

  const handleLanguageChange = async (newLanguage: string) => {
    setIsQuoteSaving(true);
    try {
      await doUpdateOutputLanguage({
        quoteId: quote.id,
        outputLanguage: newLanguage,
      });
      setQuote({ ...quote, outputLanguage: newLanguage });
      await fetchQuote(quote.id);
    } catch (err) {
      handleApiErrorToast(err);
    }
    setEditMode(false);
    setIsQuoteSaving(false);
  };

  if (!canEditOutputLanguage) {
    return (
      <MBox minW="100px" textAlign="right" mr={2}>
        <BlankTextOrContent value={quote.outputLanguage}>
          <MText color="tGray.darkPurple">
            {getLanguageLabel(quote.outputLanguage)}
          </MText>
        </BlankTextOrContent>
      </MBox>
    );
  }

  if (!editMode && !quote.outputLanguage) {
    return (
      <MBox>
        <MButton
          variant="tertiary"
          onClick={() => setEditMode(true)}
          size="sm"
          {...buttonProps}
        >
          + Add
        </MButton>
      </MBox>
    );
  }

  return (
    <MFormField
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="flex-end"
      error={errors?.outputLanguage}
      formErrorMessageProps={{
        display: 'none',
      }}
    >
      <Controller
        control={control}
        name="outputLanguage"
        render={({ field: { onChange, ...rest } }) => (
          <MCustomSelect
            isDisabled={!canEditOutputLanguage}
            isReadOnly={!canEditOutputLanguage}
            MainInputComponent={QuoteSelectInput}
            itemTitle="title"
            itemValue="value"
            items={OUTPUT_LANGUAGE_OPTIONS}
            popOverProps={{
              positioning: { placement: 'bottom-end', sameWidth: false },
            }}
            isLazy
            popOverContentProps={{ minWidth: '10rem' }}
            onChange={(e: string) => {
              onChange(e);
              handleLanguageChange(e);
            }}
            showContentInInput
            showQueryInput
            {...rest}
          />
        )}
      />
    </MFormField>
  );
};
